import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import BrandLogo from "../components/BrandLogo";
import { Check, ArrowRight, Sparkles, LayoutDashboard } from "lucide-react";
import { CONFIRMATION } from "../constants/testIds";
import { getStructuredRfq, getWorkspaceUrl, saveBuyerSession } from "../lib/api";

export default function RFQConfirmation() {
  const { id } = useParams();
  const { state: navState } = useLocation();
  const [access, setAccess] = useState(navState || null);

  useEffect(() => {
    if (access?.workspace_access_url || access?.buyer_id) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getStructuredRfq(id);
        if (cancelled || !data.buyer_complete) return;
        if (data.session_token) saveBuyerSession(data);
        setAccess({
          rfq_number: data.rfq_number,
          workspace_access_url: data.workspace_access_url,
          buyer_id: data.buyer_id,
          workspace_id: data.workspace_id,
        });
      } catch {
        /* confirmation still works without workspace link */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, access]);

  const rfqNumber = access?.rfq_number || navState?.rfq_number || id;
  const workspaceUrl = getWorkspaceUrl(access || navState);

  return (
    <div data-testid={CONFIRMATION.container} className="min-h-screen wizard-surface flex flex-col">
      <header className="glass-header">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-[4.5rem] flex items-center">
          <BrandLogo to="/" variant="default" />
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-6 py-16">
        <div className="max-w-2xl w-full">
          <div className="card-panel p-10 sm:p-12 shadow-glow text-center sm:text-left transition-all duration-500 hover:shadow-lift border-t-4 border-t-[hsl(var(--brand-red))]">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[hsl(var(--brand-navy))] to-[hsl(var(--brand-navy-mid))] grid place-items-center shadow-lift mx-auto sm:mx-0">
              <Check className="w-8 h-8 text-white" strokeWidth={2.5} />
            </div>
            <div className="mt-8 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(var(--brand-red))]/10 text-[10px] uppercase tracking-[0.24em] font-semibold text-[hsl(var(--brand-red))]">
              Submitted · {rfqNumber}
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl font-light tracking-tight leading-tight text-[hsl(var(--brand-navy))]">
              Your RFQ has been submitted.
            </h1>
            <p className="mt-6 text-lg text-neutral-600 leading-relaxed max-w-xl mx-auto sm:mx-0">
              We are matching your request with verified Turkish manufacturers. Open your workspace
              to track quotations and next steps.
            </p>

            <div className="mt-10 flex flex-wrap gap-4 justify-center sm:justify-start">
              <a
                href={workspaceUrl}
                data-testid={CONFIRMATION.workspaceBtn}
                className="inline-flex"
              >
                <Button size="lg" className="btn-wizard-primary h-14 px-8 text-base group">
                  <LayoutDashboard className="mr-2 w-5 h-5" />
                  Go to Workspace
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <Link to={`/rfq/${id}`} data-testid={CONFIRMATION.viewRfqBtn}>
                <Button size="lg" variant="outline" className="h-14 px-8 text-base">
                  View My RFQ
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex items-start gap-4 rounded-lg border border-neutral-200 bg-[hsl(var(--brand-navy))]/[0.03] p-5 text-left transition-all duration-300 hover:border-[hsl(var(--brand-red))]/25">
              <div className="icon-tile w-10 h-10 shrink-0">
                <Sparkles className="w-5 h-5 text-[hsl(var(--brand-red))]" />
              </div>
              <div>
                <div className="font-medium text-[hsl(var(--brand-navy))]">What happens next</div>
                <p className="mt-1 text-sm text-neutral-600 leading-relaxed">
                  Our sourcing team is inviting the most relevant Turkish manufacturers to quote your
                  request. Quotations typically arrive within 24–72 hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
